import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db, auth } from './firebase';
import { LANGUAGES, DEFAULT_LANGUAGE, translate } from './i18nCore';

// The React half: which language is current, where that choice is kept, and the hook screens
// use. Everything about WHAT a key means lives in i18nCore.js, which has no React in it and is
// tested on its own.

export { LANGUAGES, DEFAULT_LANGUAGE, translate } from './i18nCore';

const STORAGE_KEY = 'portal.language';
const isKnown = (code) => LANGUAGES.some(l => l.code === code);

const localeOf = (code) => (LANGUAGES.find(l => l.code === code) || LANGUAGES[0]).htmlLang;

const LanguageContext = createContext({
  language: DEFAULT_LANGUAGE,
  locale: localeOf(DEFAULT_LANGUAGE),
  setLanguage: () => {},
  setSchoolId: () => {},
  t: (path, vars, fallback) => translate(DEFAULT_LANGUAGE, path, vars, fallback),
});

const readStored = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return isKnown(v) ? v : null;
  } catch { return null; }   // private windows and blocked storage both throw
};

/**
 * Where the choice comes from, in order: what this browser last showed, then the account, then
 * English.
 *
 * Storage first ON PURPOSE. The account is the truth, but it arrives one round trip later, and a
 * screen that renders in English and then flips to Chinese is worse than one that opens in the
 * language this person last used here.
 *
 * The browser's own language is NOT consulted. English is the source language and what a
 * first-time visitor gets; somebody who wants Chinese picks it, and storage remembers from then on.
 * Opening in Chinese because the machine happens to be set to Chinese asserts a preference nobody
 * stated.
 */
export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => readStored() || DEFAULT_LANGUAGE);
  const [schoolId, setSchoolId] = useState(null);

  // The account's own preference, once there is an account and a school to read it under.
  useEffect(() => {
    const email = (auth.currentUser?.email || '').trim().toLowerCase();
    if (!email || !schoolId) return;
    getDoc(doc(db, 'schools', schoolId, 'user_preferences', email))
      .then(snap => {
        const saved = snap.exists() ? snap.data().language : null;
        if (isKnown(saved)) setLanguageState(saved);
      })
      // Not being able to read your own preference is not worth telling anybody about: the browser
      // already knows what to show.
      .catch(() => {});
  }, [schoolId]);

  useEffect(() => {
    const meta = LANGUAGES.find(l => l.code === language) || LANGUAGES[0];
    // Drives the CJK font stack and tells a screen reader which language it is reading.
    document.documentElement.lang = meta.htmlLang;
    try { localStorage.setItem(STORAGE_KEY, language); } catch { /* private window */ }
  }, [language]);

  const setLanguage = useCallback((code) => {
    if (!isKnown(code)) return;
    setLanguageState(code);
    const email = (auth.currentUser?.email || '').trim().toLowerCase();
    if (!email || !schoolId) return;
    // Written so the choice follows the person to their other devices. Failing to save is not worth
    // interrupting them for -- the language has already changed on screen.
    setDoc(doc(db, 'schools', schoolId, 'user_preferences', email), { language }, { merge: true })
      .catch(err => console.warn('Could not save the language preference', err));
  }, [schoolId]);

  const value = useMemo(() => ({
    language,
    // For toLocaleDateString and friends. Weekday and month names are the one part of the
    // interface no dictionary can carry, so they follow the chosen language rather than the
    // browser's -- otherwise a Chinese portal still says "Wednesday".
    locale: localeOf(language),
    setLanguage,
    setSchoolId,
    t: (path, vars, fallback) => translate(language, path, vars, fallback),
  }), [language, setLanguage]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

/** `const { t } = useT();` then `t('common.save')`. */
export const useT = () => useContext(LanguageContext);
